import { useState, useEffect, useCallback, useRef } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';

export const useDocumentPasteDetection = (
	contentId: string,
	userOffline: boolean,
	isEditorEmpty: boolean,
	isLivePage: boolean,
	titleTextAreaRef?: React.RefObject<HTMLTextAreaElement>,
): {
	pasteDetected: boolean;
	resetPasteDetected: () => void;
} => {
	const PASTE_CONTEXT_EXTRACTION_DELAY = 100; // Wait for paste to be applied to DOM
	const PASTE_RESET_DELAY = 500; // Used to reset paste detection
	const MIN_PASTED_CHAR_LENGTH = 50;

	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });
	const [pasteDetected, setPasteDetected] = useState(false);
	const isEditorEmptyRef = useRef(isEditorEmpty);

	useEffect(() => {
		isEditorEmptyRef.current = isEditorEmpty;
	}, [isEditorEmpty]);

	const isFeatureEnabled = isAIEnabledForContent && !userOffline;

	const findProseMirrorElement = useCallback((): HTMLElement | null => {
		const filteredProseMirrors = Array.from(document.querySelectorAll('.ProseMirror')).filter(
			(el) => !el.closest('[data-testid="chromeless-editor"]'),
		);
		return filteredProseMirrors[0] as HTMLElement | null;
	}, []);

	const resetPasteDetected = useCallback((): void => {
		setPasteDetected(false);
	}, []);

	useEffect(() => {
		if (!isFeatureEnabled) {
			return;
		}

		const proseMirrorElement = findProseMirrorElement();

		if (!proseMirrorElement) {
			return;
		}

		let postPasteReadTimeoutId: ReturnType<typeof setTimeout> | undefined;

		const handlePaste = (event: ClipboardEvent) => {
			const pastedText = event.clipboardData?.getData('text/plain') || '';
			const currentTitle = titleTextAreaRef?.current?.value ?? '';
			const wasEditorEmpty = isEditorEmptyRef.current;

			if (currentTitle.trim() !== '') {
				return;
			}

			if (wasEditorEmpty && pastedText.length > MIN_PASTED_CHAR_LENGTH) {
				if (postPasteReadTimeoutId !== undefined) {
					clearTimeout(postPasteReadTimeoutId);
				}
				postPasteReadTimeoutId = setTimeout(() => {
					postPasteReadTimeoutId = undefined;
					const extractedContext = proseMirrorElement.textContent || '';
					if (extractedContext.trim().length > 0) {
						if (
							!isLivePage ||
							(isLivePage &&
								expValEquals('confluence_ai_title_generation_on_paste', 'isEnabled', true))
						) {
							setPasteDetected(true);
						}
					}
				}, PASTE_CONTEXT_EXTRACTION_DELAY);
			}
		};

		proseMirrorElement.addEventListener('paste', handlePaste);

		return () => {
			if (postPasteReadTimeoutId !== undefined) {
				clearTimeout(postPasteReadTimeoutId);
			}
			proseMirrorElement.removeEventListener('paste', handlePaste);
		};
	}, [findProseMirrorElement, isFeatureEnabled, isLivePage, titleTextAreaRef]);

	useEffect(() => {
		if (pasteDetected) {
			const timer = setTimeout(() => setPasteDetected(false), PASTE_RESET_DELAY);
			return () => clearTimeout(timer);
		}
	}, [pasteDetected]);

	return {
		pasteDetected,
		resetPasteDetected,
	};
};
